// src/components/common/MainMenu.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    FaChevronDown,
    FaUserPlus,
    FaBars,
    FaTimes,
    FaCoins,
    FaRobot,
    FaBrain,
    FaChartLine,
    FaBullhorn,
    FaCrosshairs,
    FaEye,
    FaUsers,
    FaChartBar,
    FaTag,
    FaImage,
    FaHistory,
    FaQrcode,
    FaFileAlt,
    FaPalette,
    FaShoppingCart,
    FaUserShield,
    FaUserClock,
    FaEnvelope,
    FaCog,
    FaStore,
    FaBox,
    FaCommentAlt,
    FaBell,
    FaPoll
} from 'react-icons/fa';
import { get } from '../../helpers/apiHelper';
import useAuthStore from '../../store/authStore';
import useUserRole from '../../hooks/useUserRole';
import CrearClienteForm from '../modals/CrearClienteForm';
import SumarPuntosModal from '../modals/SumarPuntosModal';
import QRScannerModal from '../modals/QRScannerModal';

export default function MainMenu() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [showClienteForm, setShowClienteForm] = useState(false);
    const [showSumarPuntosModal, setShowSumarPuntosModal] = useState(false);
    const [showQRScanner, setShowQRScanner] = useState(false);
    const [firstMarcaId, setFirstMarcaId] = useState(null);
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { userRole } = useUserRole();

    // Obtener la primera marca del usuario para el link de infografías
    useEffect(() => {
        const fetchFirstMarca = async () => {
            try {
                if (user?.userId) {
                    const marcas = await get(`marcas/user/${user.userId}`);
                    if (marcas && marcas.length > 0) {
                        setFirstMarcaId(marcas[0]._id);
                    }
                } else {
                    // Fallback: obtener todas las marcas
                    const marcas = await get('marcas');
                    if (marcas && marcas.length > 0) {
                        setFirstMarcaId(marcas[0]._id);
                    }
                }
            } catch (error) {
                console.error('Error fetching marca for infografias:', error);
            }
        };
        fetchFirstMarca();
    }, [user]);

    const handleInfografiaClick = (e) => {
        e.preventDefault();
        if (firstMarcaId) {
            navigate(`/tienda/${firstMarcaId}/infografia`);
        } else {
            // Si no hay marca, redirigir a marcas-tiendas para que seleccione una
            navigate('/marcas-tiendas');
        }
    };

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    // Solo se encarga de abrir el modal
    const handleCrearCliente = () => {
        setShowClienteForm(true);
    };

    const handleSumarPuntos = () => {
        setShowSumarPuntosModal(true);
    };

    const handleOpenQRScanner = () => {
        setShowQRScanner(true);
    };

    const closeClienteForm = () => {
        setShowClienteForm(false);
    };

    const closeSumarPuntosModal = () => {
        setShowSumarPuntosModal(false);
    };

    return (
        <nav className="bg-white shadow top-[64px] z-30">
            <div className="max-w-7xl mx-auto px-4">
                {/* Cabecera del menú (logo + hamburguesa) */}
                <div className="flex items-center justify-between py-2">
                    {/* Botón hamburguesa (sólo mobile) */}
                    <div className="flex md:hidden">
                        <button
                            onClick={toggleMenu}
                            type="button"
                            className="text-gray-600 hover:text-gray-800 focus:outline-none"
                        >
                            {menuOpen ? (
                                <FaTimes className="text-2xl" />
                            ) : (
                                <FaBars className="text-2xl" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/** 
       *  MENÚ DESKTOP: 
       *  Aparece en una barra inferior (border-t) con dropdowns por hover 
       */}
            <div className="hidden md:block border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-4">
                    <ul className="flex items-center space-x-6 h-12">
                        {/* GESTION */}
                        <li className="relative group py-1">
                            <button className="flex items-center px-2 hover:text-gray-700 focus:outline-none">
                                <FaCog className="mr-1 text-blue-500" />
                                Gestión <FaChevronDown className="ml-1 text-sm" />
                            </button>
                            {/* Submenú: sin 'mt-x' o muy pequeño para no perder hover */}
                            <ul
                                className="absolute left-0 top-full mt-0 w-48 bg-white border border-gray-200 rounded shadow-lg 
                  opacity-0 group-hover:opacity-100 invisible group-hover:visible
                  transition-all duration-200 z-50"
                            >
                                <li>
                                    <Link
                                        to="/marcas-tiendas"
                                        className="block px-4 py-2 hover:bg-gray-100 flex items-center"
                                    >
                                        <FaStore className="mr-2 text-blue-500" />
                                        Gestión de Marcas y Tiendas
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/clientes"
                                        className="block px-4 py-2 hover:bg-gray-100 flex items-center"
                                    >
                                        <FaUsers className="mr-2 text-green-500" />
                                        Gestión de Clientes
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/inventario"
                                        className="block px-4 py-2 hover:bg-gray-100 flex items-center"
                                    >
                                        <FaBox className="mr-2 text-purple-500" />
                                        Gestión de Productos
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/data-sells"
                                        className="block px-4 py-2 hover:bg-gray-100 flex items-center"
                                    >
                                        <FaShoppingCart className="mr-2 text-blue-500" />
                                        Gestión de Ventas
                                    </Link>
                                </li>
                            </ul>
                        </li>

                        {/* MARKETING CON IA */}
                        <li className="relative group py-1">
                            <button className="flex items-center px-2 hover:text-gray-700 focus:outline-none">
                                <FaRobot className="mr-1 text-blue-500" />
                                Marketing <FaChevronDown className="ml-1 text-sm" />
                            </button>
                            <ul
                                className="absolute left-0 top-full mt-0 w-56 bg-white border border-gray-200 rounded shadow-lg 
                  opacity-0 group-hover:opacity-100 invisible group-hover:visible
                  transition-all duration-200 z-50"
                            >
                                {/* TODO: Implementar página de gestión unificada de campañas - COMENTADO HASTA COMPLETAR */}
                                {/* 
                                <li>
                                    <Link to="/campaigns" className="block px-4 py-2 hover:bg-gray-100 flex items-center">
                                        <FaBullhorn className="mr-2 text-purple-500" />
                                        Ver Todas las Campañas
                                        Estado: Implementado - CampaignsManagementPage
                                    </Link>
                                </li>
                                */}

                                <li>
                                    <Link to="/ofertas" className="block px-4 py-2 hover:bg-gray-100 flex items-center">
                                        <FaTag className="mr-2 text-orange-500" />
                                        Gestionar Ofertas
                                    </Link>
                                </li>

                                <li>
                                    <Link to="/winback" className="block px-4 py-2 hover:bg-gray-100 flex items-center">
                                        <FaUserClock className="mr-2 text-purple-500" />
                                        Recupera Clientes Perdidos
                                    </Link>
                                </li>

                                {/* TODO: Reactivar funcionalidad de IA comentada en Dashboard - COMENTADO HASTA REACTIVAR */}
                                {/* 
                                <li>
                                    <Link to="/dashboard" className="block px-4 py-2 hover:bg-gray-100 flex items-center">
                                        <FaBrain className="mr-2 text-blue-500" />
                                        Crear Campaña IA
                                        Estado: Comentado - IntelligentCampaignModal deshabilitado
                                    </Link>
                                </li>
                                */}

                                <li>
                                    <Link to="/rewards" className="block px-4 py-2 hover:bg-gray-100 flex items-center">
                                        <FaCrosshairs className="mr-2 text-green-500" />
                                        Gestionar Recompensas
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/email-marketing/campaigns" className="block px-4 py-2 hover:bg-gray-100 flex items-center">
                                        <FaEnvelope className="mr-2 text-blue-500" />
                                        Email Marketing
                                    </Link>
                                </li>
                                <li>
                                    <a
                                        href="#"
                                        onClick={handleInfografiaClick}
                                        className="block px-4 py-2 hover:bg-gray-100 flex items-center"
                                    >
                                        <FaPalette className="mr-2 text-pink-500" />
                                        Generador de Infografías
                                    </a>
                                </li>

                                {/* TODO: Añadir opciones adicionales de campañas */}
                                {/* 
                                <li>
                                    <Link to="/game-loyalty/manage" className="block px-4 py-2 hover:bg-gray-100 flex items-center">
                                        <FaGamepad className="mr-2 text-purple-500" />
                                        Campañas de Juego
                                        Estado: Implementado - Game Loyalty Campaigns
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/referral-campaigns" className="block px-4 py-2 hover:bg-gray-100 flex items-center">
                                        <FaUsers className="mr-2 text-blue-500" />
                                        Campañas de Referidos
                                        Estado: Implementado - Referral Campaigns
                                    </Link>
                                </li>
                                */}
                            </ul>
                        </li>

                        {/* ANÁLISIS Y MÉTRICAS */}
                        <li className="relative group py-1">
                            <button className="flex items-center px-2 hover:text-gray-700 focus:outline-none">
                                <FaChartLine className="mr-1 text-green-500" />
                                Analytics <FaChevronDown className="ml-1 text-sm" />
                            </button>
                            <ul
                                className="absolute left-0 top-full mt-0 w-48 bg-white border border-gray-200 rounded shadow-lg 
                  opacity-0 group-hover:opacity-100 invisible group-hover:visible
                  transition-all duration-200 z-50"
                            >
                                <li>
                                    <Link
                                        to="/statistics/dashboard"
                                        className="block px-4 py-2 hover:bg-gray-100 flex items-center"
                                    >
                                        <FaEye className="mr-2 text-blue-500" />
                                        Dashboard Analytics
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/statistics/customer"
                                        className="block px-4 py-2 hover:bg-gray-100 flex items-center"
                                    >
                                        <FaUsers className="mr-2 text-green-500" />
                                        Estadísticas Clientes
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/statistics/store"
                                        className="block px-4 py-2 hover:bg-gray-100 flex items-center"
                                    >
                                        <FaChartBar className="mr-2 text-purple-500" />
                                        Estadísticas Tienda
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/ofertas/metrics"
                                        className="block px-4 py-2 hover:bg-gray-100 flex items-center"
                                    >
                                        <FaTag className="mr-2 text-orange-500" />
                                        Métricas de Ofertas
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/statistics/reporting"
                                        className="block px-4 py-2 hover:bg-gray-100 flex items-center"
                                    >
                                        <FaFileAlt className="mr-2 text-indigo-500" />
                                        Centro de Reportes
                                    </Link>
                                </li>
                            </ul>
                        </li>

                        {/* Feedback y Alertas */}
                        <li className="relative group py-1">
                            <button className="flex items-center px-2 hover:text-gray-700 focus:outline-none">
                                <FaCommentAlt className="mr-1 text-orange-500" />
                                Feedback y Alertas <FaChevronDown className="ml-1 text-sm" />
                            </button>
                            <ul
                                className="absolute left-0 top-full mt-0 w-48 bg-white border border-gray-200 rounded shadow-lg 
                  opacity-0 group-hover:opacity-100 invisible group-hover:visible
                  transition-all duration-200 z-50"
                            >
                                <li>
                                    <Link
                                        to="/dashboard-surveys"
                                        className="block px-4 py-2 hover:bg-gray-100 flex items-center"
                                    >
                                        <FaPoll className="mr-2 text-blue-500" />
                                        Feedback
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/alertas"
                                        className="block px-4 py-2 hover:bg-gray-100 flex items-center"
                                    >
                                        <FaBell className="mr-2 text-yellow-500" />
                                        Alertas
                                    </Link>
                                </li>
                            </ul>
                        </li>


                        {/* Botones de acción a la derecha */}
                        <li className="ml-auto flex items-center gap-2">
                            <button
                                onClick={handleCrearCliente}
                                className="flex items-center bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600 transition-colors whitespace-nowrap"
                            >
                                <FaUserPlus className="mr-2" />
                                Crear Cliente
                            </button>
                            <button
                                onClick={handleSumarPuntos}
                                className="flex items-center bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600 transition-colors whitespace-nowrap"
                            >
                                <FaCoins className="mr-2" />
                                Sumar Puntos
                            </button>
                            {/* TEMPORALMENTE COMENTADO - QR Scanner con problemas de visualización de cámara */}
                            {/* 
                            <button
                                onClick={handleOpenQRScanner}
                                className="flex items-center bg-purple-500 text-white px-3 py-2 rounded hover:bg-purple-600 transition-colors whitespace-nowrap"
                            >
                                <FaQrcode className="mr-2" />
                                Escanear QR
                            </button>
                            */}
                        </li>
                    </ul>
                </div>
            </div >

            {/**
       * MENÚ MOBILE:
       * Se abre si "menuOpen" = true
       */}
            {
                menuOpen && (
                    <div className="md:hidden border-t border-gray-200">
                        <div className="px-4 pt-2 pb-4 bg-white shadow">
                            {/* Botón rápido Crear Cliente en mobile */}
                            <div className="mb-4">
                                <button
                                    onClick={() => {
                                        toggleMenu();
                                        handleCrearCliente();
                                    }}
                                    className="flex items-center bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600 transition-colors  whitespace-nowrap"
                                >
                                    <FaUserPlus className="mr-2" />
                                    Crear Cliente
                                </button>
                            </div>
                            {/* Botón "Sumar Puntos" en mobile */}
                            <div className="mb-4">
                                <button
                                    onClick={() => {
                                        toggleMenu();
                                        handleSumarPuntos();
                                    }}
                                    className="flex items-center bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600 transition-colors  whitespace-nowrap"
                                >
                                    <FaCoins className="mr-2" />
                                    Sumar Puntos
                                </button>
                            </div>
                            {/* TEMPORALMENTE COMENTADO - QR Scanner con problemas de visualización de cámara */}
                            {/* Botón "Escanear QR" en mobile */}
                            {/* 
                            <div className="mb-4">
                                <button
                                    onClick={() => {
                                        toggleMenu();
                                        handleOpenQRScanner();
                                    }}
                                    className="flex items-center bg-purple-500 text-white px-3 py-2 rounded hover:bg-purple-600 transition-colors whitespace-nowrap"
                                >
                                    <FaQrcode className="mr-2" />
                                    Escanear QR
                                </button>
                            </div>
                            */}

                            {/* Categorías en vertical */}
                            <ul className="space-y-2">
                                {/* Gestión */}
                                <li>
                                    <p className="font-semibold text-gray-800 mb-1 flex items-center">
                                        <FaCog className="mr-2 text-blue-500" />
                                        Gestión
                                    </p>
                                    <div className="ml-2 space-y-1">
                                        <Link
                                            to="/marcas-tiendas"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaStore className="mr-2 text-blue-500" />
                                            Gestión de Marcas y Tiendas
                                        </Link>
                                        <Link
                                            to="/clientes"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaUsers className="mr-2 text-green-500" />
                                            Gestión de Clientes
                                        </Link>
                                        <Link
                                            to="/inventario"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaBox className="mr-2 text-purple-500" />
                                            Gestión de Productos
                                        </Link>
                                        <Link
                                            to="/data-sells"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaShoppingCart className="mr-2 text-blue-500" />
                                            Gestión de Ventas
                                        </Link>
                                    </div>
                                </li>

                                {/* Marketing con IA */}
                                <li>
                                    <p className="font-semibold text-gray-800 mb-1">Marketing</p>
                                    <div className="ml-2 space-y-1">
                                        {/* TODO: Verificar implementación de generador de contenido - COMENTADO HASTA VERIFICAR */}
                                        {/* 
                                        <Link
                                            to="/social-content"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaImage className="mr-2 text-purple-500" />
                                            <span>Generador de Contenido Social</span>
                                            Estado: Pendiente - Verificar implementación
                                        </Link>
                                        <Link
                                            to="/social-content/history"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaHistory className="mr-2 text-gray-500" />
                                            <span>Historial de Posts</span>
                                            Estado: Pendiente - Verificar implementación
                                        </Link>
                                        */}
                                        {/* TODO: Verificar implementación de copiloto de marketing - COMENTADO HASTA VERIFICAR */}
                                        {/* 
                                        <Link
                                            to="/marketing-chain"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaRobot className="mr-2 text-blue-500" />
                                            <span>Copiloto de Marketing</span>
                                            Estado: Pendiente - Verificar implementación
                                        </Link>
                                        */}
                                        {/* TODO: Implementar gestión unificada de campañas - COMENTADO HASTA COMPLETAR */}
                                        {/* 
                                        <Link
                                            to="/campaigns"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaBullhorn className="mr-2 text-purple-500" />
                                            <span>Campañas</span>
                                            Estado: Implementado - CampaignsManagementPage
                                        </Link>
                                        */}

                                        <Link
                                            to="/ofertas"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaTag className="mr-2 text-orange-500" />
                                            Gestionar Ofertas
                                        </Link>
                                        <Link
                                            to="/winback"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaUserClock className="mr-2 text-purple-500" />
                                            WinBack Automático
                                        </Link>
                                        <Link
                                            to="/rewards"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaCrosshairs className="mr-2 text-green-500" />
                                            Gestionar Recompensas
                                        </Link>
                                        <Link
                                            to="/email-marketing/campaigns"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaEnvelope className="mr-2 text-blue-500" />
                                            Email Marketing
                                        </Link>
                                        <a
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                toggleMenu();
                                                handleInfografiaClick(e);
                                            }}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaPalette className="mr-2 text-pink-500" />
                                            Generador de Infografías
                                        </a>
                                        {/* TODO: Añadir opciones adicionales de campañas en mobile */}
                                        {/* 
                                        <Link
                                            to="/game-loyalty/manage"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaGamepad className="mr-2 text-purple-500" />
                                            <span>Campañas de Juego</span>
                                            Estado: Implementado - Game Loyalty
                                        </Link>
                                        <Link
                                            to="/referral-campaigns"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaUsers className="mr-2 text-blue-500" />
                                            <span>Campañas de Referidos</span>
                                            Estado: Implementado - Referral Campaigns
                                        </Link>
                                        */}
                                    </div>
                                </li>

                                {/* Analytics */}
                                <li>
                                    <p className="font-semibold text-gray-800 mb-1 flex items-center">
                                        <FaChartLine className="mr-2 text-green-500" />
                                        Analytics
                                    </p>
                                    <div className="ml-2 space-y-1">
                                        <Link
                                            to="/statistics/dashboard"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaEye className="mr-2 text-blue-500" />
                                            Dashboard Analytics
                                        </Link>
                                        <Link
                                            to="/statistics/customer"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaUsers className="mr-2 text-green-500" />
                                            Estadísticas Clientes
                                        </Link>
                                        <Link
                                            to="/statistics/store"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaChartBar className="mr-2 text-purple-500" />
                                            Estadísticas Tienda
                                        </Link>

                                        <Link
                                            to="/ofertas/metrics"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaTag className="mr-2 text-orange-500" />
                                            Métricas de Ofertas
                                        </Link>
                                        <Link
                                            to="/statistics/reporting"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaFileAlt className="mr-2 text-indigo-500" />
                                            Centro de Reportes
                                        </Link>
                                    </div>
                                </li>

                                {/* Feedback y Alertas */}
                                <li>
                                    <p className="font-semibold text-gray-800 mb-1 flex items-center">
                                        <FaCommentAlt className="mr-2 text-orange-500" />
                                        Feedback y Alertas
                                    </p>
                                    <div className="ml-2 space-y-1">
                                        <Link
                                            to="/dashboard-surveys"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaPoll className="mr-2 text-blue-500" />
                                            Feedback
                                        </Link>
                                        <Link
                                            to="/alertas"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded flex items-center"
                                        >
                                            <FaBell className="mr-2 text-yellow-500" />
                                            Alertas
                                        </Link>
                                    </div>
                                </li>

                                {/* Notificaciones */}
                                <li>
                                    <p className="font-semibold text-gray-800 mb-1">Notificaciones</p>
                                    <div className="ml-2 space-y-1">
                                        <Link
                                            to="/notifications/settings"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded"
                                        >
                                            Configuración
                                        </Link>
                                        <Link
                                            to="/notifications/send"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded"
                                        >
                                            Enviar Notificaciones
                                        </Link>
                                        <Link
                                            to="/notifications/history"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded"
                                        >
                                            Historial
                                        </Link>
                                        <Link
                                            to="/notifications/templates"
                                            onClick={toggleMenu}
                                            className="block px-2 py-1 hover:bg-gray-100 rounded"
                                        >
                                            Templates
                                        </Link>
                                    </div>
                                </li>

                                {/* Administración - Solo para Admins (Mobile) */}
                                {userRole === 'ADMIN_ROLE' && (
                                    <li className="mt-4 pt-4 border-t border-gray-200">
                                        <Link
                                            to="/admin"
                                            onClick={toggleMenu}
                                            className="flex items-center px-2 py-2 bg-red-50 hover:bg-red-100 rounded font-semibold text-red-600"
                                        >
                                            <FaUserShield className="mr-2 text-lg" />
                                            Panel de Administración
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div >
                )
            }
            {/* Modal para crear cliente */}
            {
                showClienteForm && (
                    <CrearClienteForm
                        marcaId={null} // Removed defaultMarcaId
                        tiendas={[]} // Removed tiendas
                        onClose={closeClienteForm}
                        onClienteCreated={() => {
                            // Acción posterior a la creación
                        }}
                    />
                )
            }
            {
                showSumarPuntosModal && (
                    <SumarPuntosModal
                        onClose={() => setShowSumarPuntosModal(false)}
                    />
                )
            }
            {
                showQRScanner && (
                    <QRScannerModal
                        isOpen={showQRScanner}
                        onClose={() => setShowQRScanner(false)}
                    />
                )
            }
        </nav >
    );
}